import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, MessageCircle as ChatCircleDots, Upload as UploadSimple, Link as LinkIcon, Sparkles as Sparkle, Trash2 as Trash, FileText, Image as ImageIcon, X, Globe, Video, PencilLine } from "lucide-react";
import { toast } from "sonner";
import InterviewModal from "../components/InterviewModal";
import PlatformsTab from "../components/PlatformsTab";
import CrossPollinationModal from "../components/CrossPollinationModal";
import SubstackConnect from "../components/SubstackConnect";

export default function BrandDetail() {
  const { brandId } = useParams();
  const [brand, setBrand] = useState(null);
  const [tab, setTab] = useState("interview"); // interview | knowledge | connect | generate
  const [knowledge, setKnowledge] = useState([]);
  const [history, setHistory] = useState([]);
  const [connections, setConnections] = useState([]);
  const [brands, setBrands] = useState([]);
  const [showInterview, setShowInterview] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genCount, setGenCount] = useState(12);
  const [genCadence, setGenCadence] = useState("weekly");
  const [launchSequence, setLaunchSequence] = useState(false);
  const [seriesMode, setSeriesMode] = useState(false);
  const [notesBatches, setNotesBatches] = useState([]);
  const [generatingNotes, setGeneratingNotes] = useState(false);
  const [posts, setPosts] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    playbooks: [],
    autopilot_enabled: false,
    substack_mission: "",
    substack_category: "",
    substack_subcategory: "",
    substack_mode: "",
    price_monthly: "",
    price_annual: "",
    price_founder: "",
    cadence_free: "",
    cadence_paid: "",
    bestseller_goal: "",
  });
  const [showXPollinate, setShowXPollinate] = useState(false);
  const [showSubstackConnect, setShowSubstackConnect] = useState(false);
  const [substackStatus, setSubstackStatus] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteForm, setNoteForm] = useState({ title: "", content: "" });
  const fileRef = useRef(null);

  const load = async () => {
    const [b, kn, hist, conns, all, p, pb] = await Promise.all([
      api.getBrand(brandId),
      api.listKnowledge(brandId),
      api.interviewHistory(brandId),
      api.listConnections(brandId),
      api.listBrands(),
      api.listContent(brandId),
      api.listPlaybooks(),
    ]);
    setBrand(b); setKnowledge(kn); setHistory(hist); setConnections(conns); setBrands(all); setPosts(p); setPlaybooks(pb);
    setSettings({
      playbooks: b.playbooks || [],
      autopilot_enabled: b.autopilot_enabled || false,
      substack_mission: b.substack_mission || "",
      substack_category: b.substack_category || "",
      substack_subcategory: b.substack_subcategory || "",
      substack_mode: b.substack_mode || "",
      price_monthly: b.price_monthly ?? "",
      price_annual: b.price_annual ?? "",
      price_founder: b.price_founder ?? "",
      cadence_free: b.cadence_free || "",
      cadence_paid: b.cadence_paid || "",
      bestseller_goal: b.bestseller_goal ?? "",
    });
    try {
      const s = await api.substackStatus(brandId);
      setSubstackStatus(s);
    } catch (_) {}
    try {
      const nb = await api.listNotesAndRestacks(brandId);
      setNotesBatches(nb);
    } catch (_) {}
  };
  useEffect(() => { load(); }, [brandId]);

  const [uploadProgress, setUploadProgress] = useState(null); // null | {pct, loaded, total, name}

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const MAX = 100 * 1024 * 1024;
    if (f.size > MAX) {
      toast.error(`File too large (${(f.size / 1024 / 1024).toFixed(1)} MB). Max 100 MB. For long videos: extract audio or split into chunks.`);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploadProgress({ pct: 0, loaded: 0, total: f.size, name: f.name });
    try {
      await api.uploadKnowledge(brandId, f, (pct, loaded, total) => {
        setUploadProgress({ pct, loaded, total, name: f.name });
      });
      toast.success(`Uploaded & extracted: ${f.name}`);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploadProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addConnection = async (target_brand_id, notes) => {
    if (target_brand_id === brandId) return toast.error("Cannot connect to itself");
    await api.addConnection(brandId, { target_brand_id, notes, enabled: true });
    load();
    toast.success("Connection added");
  };

  const [genJob, setGenJob] = useState(null);
  const [stale, setStale] = useState({ stale: 0, total: 0 });
  const [refreshing, setRefreshing] = useState(false);
  const [enabledPlatformCount, setEnabledPlatformCount] = useState(0);

  // poll stale count
  useEffect(() => {
    if (!brandId) return;
    let alive = true;
    const fetchStale = () => api.staleCount(brandId).then((s) => alive && setStale(s)).catch(() => {});
    fetchStale();
    const id = setInterval(fetchStale, 15000);
    api.listBrandPlatforms(brandId).then((p) => {
      const enabled = (p || []).filter((x) => x.enabled && x.id !== "substack").length;
      setEnabledPlatformCount(enabled);
    }).catch(() => {});
    return () => { alive = false; clearInterval(id); };
  }, [brandId]);

  const generate = async (extra = { pollination_brand_ids: [], pollination_mode: "passive" }) => {
    setGenerating(true);
    try {
      const job = await api.generateYear(brandId, {
        count: genCount,
        cadence: genCadence,
        include_platforms: true,
        launch_sequence: launchSequence,
        series_mode: seriesMode,
        ...extra,
      });
      setGenJob(job);
      const platformTotal = (job.platform_count || 0) * genCount;
      const px = pollination.pollination_brand_ids?.length || 0;
      const polMsg = px > 0 ? ` · ${pollination.pollination_mode} mode w/ ${px} partner${px > 1 ? "s" : ""}` : "";
      toast.success(`Job queued: ${genCount} Substack posts${platformTotal ? ` + ${platformTotal} platform adaptations` : ""}${polMsg} — generating in background…`);
      const poll = setInterval(async () => {
        try {
          const j = await api.getJob(job.job_id);
          setGenJob(j);
          load();
          if (j.status === "completed" || j.status === "failed") {
            clearInterval(poll);
            setGenerating(false);
            if (j.status === "completed") toast.success(`Generated ${j.completed} Substack posts (+ platform versions) — review in Approvals`);
            else toast.error(`Job failed: ${j.error || "unknown"}`);
          }
        } catch {
          clearInterval(poll);
          setGenerating(false);
        }
      }, 3000);
    } catch {
      toast.error("Could not start generation");
      setGenerating(false);
    }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    toast.loading("Refreshing pending posts with new context…", { id: "rf" });
    try {
      const r = await api.refreshPending(brandId);
      toast.success(`Refreshed ${r.refreshed} posts (+ ${r.platform_count} platform adaptations each)`, { id: "rf" });
      const s = await api.staleCount(brandId);
      setStale(s);
      load();
    } catch (e) {
      toast.error("Refresh failed", { id: "rf" });
    } finally {
      setRefreshing(false);
    }
  };

  if (!brand) return <div className="text-eyebrow">Loading…</div>;

  return (
    <div data-testid="brand-detail-page" className="max-w-6xl">
      <Link to="/brands" className="text-eyebrow flex items-center gap-1 mb-6 hover:text-[#002FA7]" data-testid="back-to-brands">
        <ArrowLeft size={14} /> All brands
      </Link>

      <div className="flex items-center gap-4 mb-2">
        <div className="w-16 h-16" style={{ background: brand.color }} />
        <div className="flex-1">
          <div className="text-eyebrow">{brand.is_client ? "Client Brand" : "Owned Brand"}</div>
          <h1 className="font-display font-black text-5xl tracking-tighter">{brand.name}</h1>
        </div>
        <Link to={`/onboarding?brand_id=${brandId}`} data-testid="brand-substack-setup-link"
              className="border border-black px-4 py-2 text-sm hover:bg-[#F4F5F6]">
          Substack setup
        </Link>
        <button onClick={() => setShowSettings(true)} data-testid="brand-settings-button"
                className="border border-black px-4 py-2 text-sm hover:bg-[#F4F5F6]">
          Settings
        </button>
      </div>
      <p className="text-[#52525B] mt-2 mb-6 max-w-3xl">{brand.sells || brand.description}</p>

      <div className="grid grid-cols-4 gap-0 mb-10 border-l border-t border-[#E2E4E9]">
        <Stat l="Knowledge" v={`${brand.knowledge_score ?? 0}/100`} />
        <Stat l="Interview Q&As" v={history.length} />
        <Stat l="Knowledge files" v={knowledge.length} />
        <Stat l="Generated posts" v={posts.length} />
      </div>

      <div className="flex border-b border-[#0A0A0B] mb-8 overflow-x-auto">
        {[
          ["interview", "AI Interview", ChatCircleDots],
          ["knowledge", "Knowledge", FileText],
          ["connect", "Cross-pollination", LinkIcon],
          ["generate", "Generate Content", Sparkle],
          ["platforms", "Platforms", Globe],
        ].map(([k, label, Icon]) => (
          <button
            key={k}
            data-testid={`tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-5 py-3 flex items-center gap-2 text-sm border-r border-[#E2E4E9] whitespace-nowrap ${
              tab === k ? "bg-[#0A0A0B] text-white" : "bg-white hover:bg-[#F4F5F6]"
            }`}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {tab === "interview" && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display font-bold text-3xl tracking-tight">Iterative interview</h2>
              <p className="text-sm text-[#52525B] mt-1">20 questions per session. Each one based on your last answer.</p>
            </div>
            <button
              onClick={() => setShowInterview(true)}
              data-testid="start-interview-button"
              className="bg-[#0A0A0B] text-white px-5 py-3 text-sm hover:bg-[#002FA7]"
            >
              {history.length ? "Continue interview" : "Start interview"}
            </button>
          </div>
          {history.length === 0 ? (
            <div className="border-2 border-dashed border-[#E2E4E9] p-12 text-center">
              <ChatCircleDots size={48} className="mx-auto text-[#A1A1AA]" />
              <p className="font-display font-bold text-2xl mt-4">No answers yet</p>
              <p className="text-sm text-[#52525B] mt-2">Run a session to fill the brand profile.</p>
            </div>
          ) : (
            <div className="space-y-4 max-w-3xl">
              {history.slice().reverse().map((qa) => (
                <div key={qa.qa_id} className="border border-[#E2E4E9] p-5">
                  <div className="text-eyebrow mb-2">Question</div>
                  <div className="font-display font-semibold text-lg">{qa.question}</div>
                  <div className="text-eyebrow mt-4 mb-2">Your answer</div>
                  <div className="text-sm text-[#0A0A0B] whitespace-pre-wrap">{qa.answer}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "knowledge" && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display font-bold text-3xl tracking-tight">Knowledge base</h2>
              <p className="text-sm text-[#52525B] mt-1">Upload PDFs, docs, images. We extract & feed the writer.</p>
            </div>
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" hidden onChange={upload} data-testid="knowledge-file-input"
                     accept=".pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.webp,.gif,.mp3,.wav,.m4a,.ogg,.flac,.mp4,.mov,.webm,.avi,.mkv" />
              <button onClick={() => { setNoteForm({ title: "", content: "" }); setShowNoteModal(true); }}
                      data-testid="write-note-button"
                      className="bg-white border border-black text-[#0A0A0B] px-5 py-3 text-sm hover:bg-[#F4F5F6] flex items-center gap-2">
                <PencilLine size={16} /> Write a note
              </button>
              <button onClick={() => fileRef.current?.click()} data-testid="upload-knowledge-button"
                      className="bg-[#0A0A0B] text-white px-5 py-3 text-sm hover:bg-[#002FA7] flex items-center gap-2">
                <UploadSimple size={16} /> Upload file
              </button>
            </div>
          </div>
          <div
            className="border-2 border-dashed border-[#E2E4E9] hover:border-[#002FA7] p-8 mb-6 cursor-pointer text-center transition-colors"
            onClick={() => fileRef.current?.click()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) { const fake = { target: { files: [f] } }; upload(fake); } }}
            onDragOver={(e) => e.preventDefault()}
            data-testid="upload-dropzone"
          >
            <UploadSimple size={32} className="mx-auto text-[#A1A1AA]" />
            <p className="mt-2 text-sm text-[#52525B]">Drop a file or click to upload</p>
            <p className="mt-1 text-xs text-[#A1A1AA]">PDF · DOCX · TXT · MD · PNG/JPG/WebP · MP3/WAV/M4A · MP4/MOV/WebM · Auto-detected. Videos can be repurposed into Substack drafts.</p>
            <p className="mt-2 text-[10px] mono uppercase text-[#A1A1AA]">Max 100 MB · Audio/video up to ~90 min</p>
          </div>
          {uploadProgress && (
            <div className="border-2 border-black bg-white p-4 mb-6" data-testid="upload-progress">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold truncate mr-3">{uploadProgress.name}</div>
                <div className="mono text-xs">{uploadProgress.pct}% · {(uploadProgress.loaded / 1024 / 1024).toFixed(1)}/{(uploadProgress.total / 1024 / 1024).toFixed(1)} MB</div>
              </div>
              <div className="h-2 bg-[#F4F5F6] border border-black overflow-hidden">
                <div className="h-full bg-[#F59E0B] transition-all" style={{ width: `${uploadProgress.pct}%` }}/>
              </div>
              {uploadProgress.pct === 100 && (
                <div className="mt-2 text-xs mono text-[#52525B]">Uploaded — server is now extracting content (may take a moment for big files)…</div>
              )}
            </div>
          )}
          {knowledge.length === 0 ? (
            <p className="text-sm text-[#52525B]">No knowledge yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-0 border-l border-t border-[#E2E4E9]">
              {knowledge.map((k) => (
                <KnowledgeCard key={k.knowledge_id} k={k} onChanged={load} />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "connect" && (
        <div>
          <h2 className="font-display font-bold text-3xl tracking-tight mb-2">Cross-pollination</h2>
          <p className="text-sm text-[#52525B] mb-6">Pick brands this one can borrow context from. Add notes about how they should talk to each other.</p>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-eyebrow mb-3">Available brands</div>
              <div className="space-y-2">
                {brands.filter((b) => b.brand_id !== brandId && !connections.some((c) => c.target_brand_id === b.brand_id)).map((b) => (
                  <ConnectRow key={b.brand_id} b={b} onAdd={(notes) => addConnection(b.brand_id, notes)} />
                ))}
                {brands.length <= 1 && <p className="text-sm text-[#52525B]">Add more brands to connect.</p>}
              </div>
            </div>
            <div>
              <div className="text-eyebrow mb-3">Active connections</div>
              {connections.length === 0 ? (
                <p className="text-sm text-[#52525B]">None yet.</p>
              ) : (
                <div className="space-y-2">
                  {connections.map((c) => {
                    const t = brands.find((b) => b.brand_id === c.target_brand_id);
                    return (
                      <div key={c.connection_id} className="border border-[#E2E4E9] p-4">
                        <div className="flex items-center justify-between">
                          <div className="font-semibold">{t?.name || "Brand"}</div>
                          <button onClick={async () => { await api.removeConnection(brandId, c.connection_id); load(); }}
                                  data-testid={`remove-connection-${c.connection_id}`}>
                            <X size={16} />
                          </button>
                        </div>
                        {c.notes && <p className="text-xs text-[#52525B] mt-2 whitespace-pre-wrap">{c.notes}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "generate" && (
        <div>
          <h2 className="font-display font-bold text-3xl tracking-tight mb-2">Generate viral content</h2>
          <p className="text-sm text-[#52525B] mb-6 max-w-2xl">One click → a year of Substack posts <strong>plus</strong> auto-adapted versions for every platform you've enabled for this brand. As you keep doing interviews and uploading new content, pending posts get flagged stale and you can refresh them with one tap so unlaunched content gets <em>better</em> as your brand learns.</p>

          {stale.stale > 0 && (
            <div className="border-2 border-[#F59E0B] bg-[#FFF7ED] p-4 mb-6 flex items-center justify-between gap-4" data-testid="stale-banner">
              <div>
                <div className="font-display font-bold text-lg">{stale.stale} of {stale.total} pending posts are stale</div>
                <p className="text-sm text-[#52525B] mt-1">You've added new interview answers, knowledge, or accepted flags since these drafts were written. Refresh to weave the new context in.</p>
              </div>
              <button onClick={refreshAll} disabled={refreshing} data-testid="refresh-pending-button"
                      className="bg-[#F59E0B] text-white px-5 py-3 text-sm hover:bg-[#D97706] disabled:opacity-50 flex items-center gap-2 whitespace-nowrap">
                <Sparkle size={16} /> {refreshing ? "Refreshing…" : `Refresh ${stale.stale}`}
              </button>
            </div>
          )}

          <div className="border-2 border-black brutal-shadow p-6 max-w-2xl">
            <div className="text-eyebrow mb-2">One-click year generator</div>
            <h3 className="font-display font-bold text-2xl tracking-tight mb-4">Substack-first · multiplies into {enabledPlatformCount} other platform{enabledPlatformCount === 1 ? "" : "s"}</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-eyebrow block mb-2">Number of Substack posts</label>
                <input type="number" min={1} max={52} value={genCount} onChange={(e) => setGenCount(parseInt(e.target.value || "1"))}
                       data-testid="gen-count-input"
                       className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none font-display text-xl" />
              </div>
              <div>
                <label className="text-eyebrow block mb-2">Cadence</label>
                <select value={genCadence} onChange={(e) => setGenCadence(e.target.value)}
                        data-testid="gen-cadence-select"
                        className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly (recommended)</option>
                  <option value="biweekly">Biweekly</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-4 mb-4 text-xs text-[#52525B]">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="launch-sequence-toggle-wrap">
                <input type="checkbox" checked={launchSequence} onChange={(e) => setLaunchSequence(e.target.checked)} data-testid="launch-sequence-toggle" />
                <span>Lead with the 6-post launch sequence (Manifesto → Origin → Promise → Free Anchor → Paid Preview → Invitation)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="series-mode-toggle-wrap">
                <input type="checkbox" checked={seriesMode} onChange={(e) => setSeriesMode(e.target.checked)} data-testid="series-mode-toggle" />
                <span>Course/series mode (Next/Previous, Start here)</span>
              </label>
            </div>
            <button onClick={() => setShowXPollinate(true)} disabled={generating}
                    data-testid="generate-year-button"
                    className="w-full bg-[#0A0A0B] text-white px-5 py-4 text-base hover:bg-[#002FA7] disabled:opacity-50 flex items-center justify-center gap-2">
              <Sparkle size={18} /> {generating ? "Generating in background…" : `Generate a year of viral content${enabledPlatformCount ? ` (${genCount} Substack + ${genCount * enabledPlatformCount} platform versions)` : ` (${genCount} Substack posts)`}`}
            </button>
            <p className="text-xs text-[#A1A1AA] mt-3">Substack is the source of truth. Each post is engineered for viral sales using your brand interview answers, knowledge base, accepted flags, top-performing analytics, and viral playbooks — then automatically adapted to every enabled platform.</p>
            {genJob && (
              <div className="mt-4 border border-[#E2E4E9] p-3" data-testid="job-progress">
                <div className="flex items-center justify-between text-xs mono">
                  <span>Job {genJob.job_id.slice(-6)}</span>
                  <span className="uppercase">{genJob.status}</span>
                </div>
                <div className="h-2 bg-[#F4F5F6] mt-2 relative">
                  <div className="absolute inset-y-0 left-0 bg-[#0A0A0B]" style={{ width: `${(genJob.completed / Math.max(1, genJob.total)) * 100}%` }} />
                </div>
                <div className="text-xs mono text-[#A1A1AA] mt-1">{genJob.completed}/{genJob.total} Substack posts · {genJob.platform_count || 0} platforms per post</div>
              </div>
            )}
          </div>

          <div className="mt-8 max-w-3xl border-l-4 border-[#F59E0B] pl-5 py-4" data-testid="notes-restacks-panel">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-eyebrow mb-1">Relational layer · human-action items</div>
                <h3 className="font-display font-bold text-2xl tracking-tight">Substack Notes & Restacks</h3>
              </div>
              <button
                onClick={async () => {
                  setGeneratingNotes(true);
                  try {
                    const r = await api.generateNotesAndRestacks(brandId, { count_notes: 8, count_restacks: 6 });
                    setNotesBatches((prev) => [r, ...prev]);
                    toast.success(`Generated ${r.notes.length} Notes + ${r.restacks.length} restack prompts.`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Notes generation failed");
                  } finally {
                    setGeneratingNotes(false);
                  }
                }}
                disabled={generatingNotes}
                data-testid="generate-notes-button"
                className="px-4 py-2 text-sm border-2 border-[#0A0A0B] hover:bg-[#0A0A0B] hover:text-white disabled:opacity-50"
              >
                {generatingNotes ? "Generating…" : "Generate Notes & Restacks"}
              </button>
            </div>
            <p className="text-xs text-[#52525B] mb-4">
              These are <strong>not auto-posts</strong>. They're short Notes in brand voice + restack prompts for you to act on yourself — Substack's social graph rewards human presence, not bots.
            </p>
            {notesBatches.length === 0 ? (
              <div className="text-xs text-[#A1A1AA]">No batches yet. Generate one above.</div>
            ) : (
              <div className="space-y-4">
                {notesBatches.slice(0, 1).map((b) => (
                  <div key={b.batch_id} className="space-y-3" data-testid={`notes-batch-${b.batch_id}`}>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs mono uppercase text-[#52525B] mb-2">Notes ({b.notes.length})</div>
                        <ul className="space-y-2">
                          {b.notes.map((n) => (
                            <li key={n.note_id} className="text-xs border border-[#E2E4E9] p-2 leading-snug" data-testid={`note-${n.note_id}`}>
                              <span className="mono uppercase text-[10px] text-[#A1A1AA] mr-2">{n.tier || "free"}</span>
                              {n.text}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div className="text-xs mono uppercase text-[#52525B] mb-2">Restack / engage ({b.restacks.length})</div>
                        <ul className="space-y-2">
                          {b.restacks.map((r) => (
                            <li key={r.action_id} className="text-xs border border-[#E2E4E9] p-2 leading-snug" data-testid={`restack-${r.action_id}`}>
                              <div className="mono uppercase text-[10px] text-[#A1A1AA]">{r.action} → {r.target}</div>
                              <div className="mt-1">{r.angle}</div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="text-[10px] mono uppercase text-[#A1A1AA]">
                      batch {b.batch_id.slice(-8)} · {b.human_action_required ? "human-action" : "auto"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {posts.length > 0 && (
            <div className="mt-8 max-w-3xl">
              <div className="text-eyebrow mb-3">Recent drafts ({posts.length})</div>
              <div className="space-y-2">
                {posts.slice(0, 6).map((p) => (
                  <Link key={p.post_id} to={`/approvals?post=${p.post_id}`} className="block border border-[#E2E4E9] p-4 hover:border-[#0A0A0B]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs mono text-[#A1A1AA]">{(p.scheduled_date || "").slice(0, 10)}</span>
                      <span className="text-xs mono uppercase">{p.status}{p.stale ? " · STALE" : ""}{p.auto_refreshed_at ? " · REFRESHED" : ""}</span>
                    </div>
                    <div className="font-semibold">{p.title}</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showInterview && (
        <InterviewModal brandId={brandId} onClose={() => { setShowInterview(false); load(); }} />
      )}

      {tab === "platforms" && (
        <PlatformsTab brandId={brandId} brand={brand} posts={posts} />
      )}

      {showSettings && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowSettings(false)}>
          <div className="bg-white border-2 border-black w-full max-w-2xl p-8 brutal-shadow-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-bold text-3xl tracking-tight">Brand settings</h2>
              <button onClick={() => setShowSettings(false)} data-testid="close-settings"><X size={20} /></button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="text-eyebrow block mb-2">Substack publishing</label>
                {substackStatus?.connected ? (
                  <div className="border border-[#10B981] bg-[#ECFDF5] p-3 flex items-start justify-between gap-3"
                       data-testid="brand-substack-connected">
                    <div className="text-sm">
                      <div className="font-semibold">Connected</div>
                      <div className="text-xs text-[#52525B] break-all">{substackStatus.publication_url}</div>
                      {substackStatus.login_email && (
                        <div className="text-xs text-[#A1A1AA]">{substackStatus.login_email}</div>
                      )}
                    </div>
                    <button
                      onClick={() => setShowSubstackConnect(true)}
                      data-testid="brand-substack-manage-btn"
                      className="text-xs underline whitespace-nowrap"
                    >Manage</button>
                  </div>
                ) : (
                  <div className="border border-[#0A0A0B] p-3 flex items-start justify-between gap-3"
                       data-testid="brand-substack-disconnected">
                    <div className="text-sm">
                      <div className="font-semibold">Not connected</div>
                      <div className="text-xs text-[#52525B] mt-1">
                        Posts will auto-draft in Substack — you click <em>Publish</em> there in one tap.
                      </div>
                    </div>
                    <button
                      onClick={() => setShowSubstackConnect(true)}
                      data-testid="brand-substack-connect-btn"
                      className="px-3 py-2 text-xs bg-[#0A0A0B] text-white hover:bg-[#002FA7] whitespace-nowrap"
                    >Connect Substack</button>
                  </div>
                )}
              </div>
              <div>
                <label className="text-eyebrow block mb-2">Autopilot</label>
                <label className="flex items-start gap-3 p-3 border border-[#E2E4E9] cursor-pointer hover:border-black" data-testid="autopilot-toggle-row">
                  <input
                    type="checkbox"
                    checked={settings.autopilot_enabled}
                    onChange={(e) => setSettings({ ...settings, autopilot_enabled: e.target.checked })}
                    className="mt-1"
                    data-testid="autopilot-toggle"
                  />
                  <div className="text-sm">
                    <div className="font-semibold">Auto-ship approved posts at their scheduled time</div>
                    <div className="text-xs text-[#52525B] mt-1">When ON, the system checks every minute for approved posts whose scheduled_date is past, auto-creates a Substack draft (1-click publish for you), and ships every other connected platform via Ayrshare.</div>
                  </div>
                </label>
              </div>
              <div>
                <label className="text-eyebrow block mb-2">Substack strategy</label>
                <p className="text-xs text-[#52525B] mb-3">Per the <strong>Substack Strategy — Foundation</strong> doc. These shape how this publication positions, prices, and paces.</p>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs mono uppercase text-[#52525B] block mb-1">One-line mission</label>
                    <input
                      value={settings.substack_mission}
                      onChange={(e) => setSettings({ ...settings, substack_mission: e.target.value })}
                      placeholder="e.g. Help indie founders ship without burnout"
                      data-testid="substack-mission"
                      className="w-full border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs mono uppercase text-[#52525B] block mb-1">Category</label>
                      <input value={settings.substack_category}
                             onChange={(e) => setSettings({ ...settings, substack_category: e.target.value })}
                             placeholder="Business" data-testid="substack-category"
                             className="w-full border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none" />
                    </div>
                    <div>
                      <label className="text-xs mono uppercase text-[#52525B] block mb-1">Subcategory</label>
                      <input value={settings.substack_subcategory}
                             onChange={(e) => setSettings({ ...settings, substack_subcategory: e.target.value })}
                             placeholder="Entrepreneurship" data-testid="substack-subcategory"
                             className="w-full border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs mono uppercase text-[#52525B] block mb-1">Identity mode</label>
                    <select value={settings.substack_mode}
                            onChange={(e) => setSettings({ ...settings, substack_mode: e.target.value })}
                            data-testid="substack-mode"
                            className="w-full border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none bg-white">
                      <option value="">— pick one —</option>
                      <option value="faceless">Faceless (movement brand)</option>
                      <option value="face">Face (visible founder)</option>
                      <option value="blend">Blend (movement + founder face — maximises monetization)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs mono uppercase text-[#52525B] block mb-1">$ monthly</label>
                      <input type="number" step="1" min="0" value={settings.price_monthly}
                             onChange={(e) => setSettings({ ...settings, price_monthly: e.target.value })}
                             placeholder="5" data-testid="price-monthly"
                             className="w-full border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none" />
                    </div>
                    <div>
                      <label className="text-xs mono uppercase text-[#52525B] block mb-1">$ annual</label>
                      <input type="number" step="1" min="0" value={settings.price_annual}
                             onChange={(e) => setSettings({ ...settings, price_annual: e.target.value })}
                             placeholder="50" data-testid="price-annual"
                             className="w-full border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none" />
                    </div>
                    <div>
                      <label className="text-xs mono uppercase text-[#52525B] block mb-1">$ founder/yr</label>
                      <input type="number" step="1" min="0" value={settings.price_founder}
                             onChange={(e) => setSettings({ ...settings, price_founder: e.target.value })}
                             placeholder="300" data-testid="price-founder"
                             className="w-full border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none" />
                    </div>
                  </div>
                  <p className="text-[10px] text-[#A1A1AA]">Single-digit monthly converts; $10+ suppresses. Raise ~$1-3 every 6 months. Always offer annual. Founder ~$200-500/yr unlocks all founder content.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs mono uppercase text-[#52525B] block mb-1">Free cadence</label>
                      <input value={settings.cadence_free}
                             onChange={(e) => setSettings({ ...settings, cadence_free: e.target.value })}
                             placeholder="monthly anchor" data-testid="cadence-free"
                             className="w-full border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none" />
                    </div>
                    <div>
                      <label className="text-xs mono uppercase text-[#52525B] block mb-1">Paid cadence</label>
                      <input value={settings.cadence_paid}
                             onChange={(e) => setSettings({ ...settings, cadence_paid: e.target.value })}
                             placeholder="weekly" data-testid="cadence-paid"
                             className="w-full border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs mono uppercase text-[#52525B] block mb-1">Bestseller paid-sub goal</label>
                    <input type="number" step="1" min="0" value={settings.bestseller_goal}
                           onChange={(e) => setSettings({ ...settings, bestseller_goal: e.target.value })}
                           placeholder="100 / 1000 / 10000" data-testid="bestseller-goal"
                           className="w-full border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none" />
                    <p className="text-[10px] text-[#A1A1AA] mt-1">100+ paid = New Bestseller · 1,000+ = full badge · 10,000+ = top badge.</p>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-eyebrow block mb-3">Viral playbook library</label>
                <p className="text-xs text-[#52525B] mb-3">Pick frameworks the writer will rotate through. Every post will use exactly one.</p>
                <div className="space-y-2">
                  {playbooks.map((p) => {
                    const checked = settings.playbooks.includes(p.id);
                    return (
                      <label key={p.id} data-testid={`playbook-toggle-${p.id}`}
                             className={`flex items-start gap-3 p-3 border cursor-pointer ${checked ? "border-[#0A0A0B] bg-[#F4F5F6]" : "border-[#E2E4E9] hover:border-black"}`}>
                        <input type="checkbox" checked={checked} onChange={(e) => {
                          const next = e.target.checked
                            ? [...settings.playbooks, p.id]
                            : settings.playbooks.filter((x) => x !== p.id);
                          setSettings({ ...settings, playbooks: next });
                        }} className="mt-1" />
                        <div className="flex-1">
                          <div className="font-semibold">{p.name}</div>
                          <div className="text-xs text-[#52525B]">{p.desc}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="mt-8 flex gap-3 justify-end">
              <button onClick={() => setShowSettings(false)} className="px-5 py-3 text-sm border border-black hover:bg-[#F4F5F6]">Cancel</button>
              <button onClick={async () => {
                const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
                await api.updateBrand(brandId, {
                  ...brand,
                  playbooks: settings.playbooks,
                  autopilot_enabled: settings.autopilot_enabled,
                  substack_mission: settings.substack_mission || null,
                  substack_category: settings.substack_category || null,
                  substack_subcategory: settings.substack_subcategory || null,
                  substack_mode: settings.substack_mode || null,
                  price_monthly: numOrNull(settings.price_monthly),
                  price_annual: numOrNull(settings.price_annual),
                  price_founder: numOrNull(settings.price_founder),
                  cadence_free: settings.cadence_free || null,
                  cadence_paid: settings.cadence_paid || null,
                  bestseller_goal: numOrNull(settings.bestseller_goal),
                });
                toast.success("Saved");
                setShowSettings(false);
                load();
              }} data-testid="save-settings-button" className="px-5 py-3 text-sm bg-[#0A0A0B] text-white hover:bg-[#002FA7]">Save</button>
            </div>
          </div>
        </div>
      )}

      <CrossPollinationModal
        open={showXPollinate}
        brandId={brandId}
        onClose={() => setShowXPollinate(false)}
        onConfirm={(payload) => {
          setShowXPollinate(false);
          generate(payload);
        }}
      />

      <SubstackConnect
        open={showSubstackConnect}
        brandId={brandId}
        brand={brand}
        onClose={() => setShowSubstackConnect(false)}
        onConnected={(s) => { setSubstackStatus(s); }}
      />

      {showNoteModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             data-testid="write-note-modal"
             onClick={() => setShowNoteModal(false)}>
          <div className="bg-white border-2 border-black w-full max-w-2xl p-7 brutal-shadow-lg max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-eyebrow text-[#A1A1AA] flex items-center gap-2"><PencilLine size={14} /> Knowledge note</div>
              <button onClick={() => setShowNoteModal(false)} aria-label="Close" data-testid="close-note-modal"><X size={18} /></button>
            </div>
            <h2 className="font-display font-black text-3xl tracking-tighter mb-2">Write in knowledge</h2>
            <p className="text-sm text-[#52525B] mb-5">
              Paste a story, a voice rule, an offer, a customer quote — anything the AI should know about this brand. Treated as a real knowledge source for every future post.
            </p>
            <label className="text-eyebrow block mb-2">Title (optional)</label>
            <input
              value={noteForm.title}
              onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
              placeholder="e.g. Brand voice rules"
              data-testid="note-title"
              className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none mb-4"
            />
            <label className="text-eyebrow block mb-2">Content</label>
            <textarea
              value={noteForm.content}
              onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
              rows={10}
              placeholder="Paste or type anything here…"
              data-testid="note-content"
              className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none"
            />
            <div className="flex gap-3 justify-end mt-5">
              <button onClick={() => setShowNoteModal(false)} className="px-4 py-2 text-sm border border-black hover:bg-[#F4F5F6]">Cancel</button>
              <button
                onClick={async () => {
                  if (!noteForm.content.trim()) return toast.error("Add some content first");
                  try {
                    await api.addTextKnowledge(brandId, noteForm.title.trim(), noteForm.content.trim());
                    toast.success("Saved — feeding the writer now.");
                    setShowNoteModal(false);
                    setNoteForm({ title: "", content: "" });
                    load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Could not save note");
                  }
                }}
                data-testid="save-note-button"
                className="px-5 py-3 text-sm bg-[#0A0A0B] text-white hover:bg-[#002FA7]"
              >
                Add to knowledge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ l, v }) {
  return (
    <div className="border-r border-b border-[#E2E4E9] p-5">
      <div className="text-eyebrow">{l}</div>
      <div className="font-display font-black text-3xl mt-1 tracking-tighter">{v}</div>
    </div>
  );
}

function ConnectRow({ b, onAdd }) {
  const [notes, setNotes] = useState("");
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-[#E2E4E9] p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6" style={{ background: b.color }} />
          <span className="font-medium">{b.name}</span>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-eyebrow hover:text-[#002FA7]" data-testid={`expand-connect-${b.brand_id}`}>
          {expanded ? "Cancel" : "Connect"}
        </button>
      </div>
      {expanded && (
        <div className="mt-3 space-y-2">
          <textarea placeholder="Notes about how these should talk to each other…"
                    value={notes} onChange={(e) => setNotes(e.target.value)}
                    data-testid={`connect-notes-${b.brand_id}`}
                    className="w-full border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none resize-none" rows={2} />
          <button onClick={() => onAdd(notes)} data-testid={`confirm-connect-${b.brand_id}`}
                  className="bg-[#0A0A0B] text-white text-xs px-4 py-2">Add connection</button>
        </div>
      )}
    </div>
  );
}

function KnowledgeCard({ k, onChanged }) {
  const [note, setNote] = useState(k.user_note || "");
  const [desiredNext, setDesiredNext] = useState(k.desired_next || "");
  const [busy, setBusy] = useState(false);
  const [showNote, setShowNote] = useState(false);

  const Icon = k.kind === "image" ? ImageIcon : k.kind === "video" ? Video : k.kind === "audio" ? UploadSimple : FileText;

  const repurpose = async () => {
    setBusy(true);
    toast.loading("Extracting audio + transcribing + drafting…", { id: "rp" });
    try {
      const r = await api.repurposeVideo(k.knowledge_id);
      toast.success(`Repurposed — Substack draft "${r.post?.title || 'created'}" is in your approval queue`, { id: "rp" });
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Repurpose failed", { id: "rp" });
    } finally {
      setBusy(false);
    }
  };

  const saveNote = async () => {
    await api.addUploadNote(k.knowledge_id, note, desiredNext);
    toast.success("Note saved");
    onChanged();
  };

  return (
    <div className="p-5 border-r border-b border-[#E2E4E9]" data-testid={`knowledge-card-${k.knowledge_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={18} />
          <span className="font-semibold truncate max-w-[18ch]">{k.filename}</span>
        </div>
        <button onClick={async () => { await api.deleteKnowledge(k.knowledge_id); onChanged(); }} data-testid={`delete-knowledge-${k.knowledge_id}`}>
          <Trash size={14} className="text-[#A1A1AA] hover:text-[#E11D48]" />
        </button>
      </div>
      <div className="text-xs mono text-[#A1A1AA] mb-2 uppercase">{k.kind} · {Math.round((k.size || 0) / 1024)}kb {k.user_note && "· noted"}</div>
      {k.preview && <p className="text-xs text-[#52525B] line-clamp-4">{k.preview}</p>}
      {k.kind === "video" && (
        <div className="mt-3 flex flex-wrap gap-1">
          {k.repurpose_post_id ? (
            <span className="text-xs mono text-[#059669] uppercase">✓ Repurposed</span>
          ) : (
            <button onClick={repurpose} disabled={busy} data-testid={`repurpose-${k.knowledge_id}`}
                    className="px-2 py-1 text-xs bg-[#0A0A0B] text-white hover:bg-[#002FA7] disabled:opacity-50 flex items-center gap-1">
              <Sparkle size={10} /> {busy ? "Processing…" : "Repurpose (audio + transcript + draft)"}
            </button>
          )}
        </div>
      )}
      <div className="mt-2">
        <button onClick={() => setShowNote(!showNote)} className="text-xs mono uppercase text-[#52525B] hover:text-black" data-testid={`note-toggle-${k.knowledge_id}`}>
          {showNote ? "Hide note" : (k.user_note ? "Edit note ▾" : "Add note ▾")}
        </button>
        {showNote && (
          <div className="mt-2 space-y-1">
            <textarea
              placeholder="Notes about this upload (context, source, etc.)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid={`note-${k.knowledge_id}`}
              rows={2}
              className="w-full border border-[#E2E4E9] focus:border-black p-2 text-xs outline-none resize-none"
            />
            <input
              placeholder="Desired next action (e.g. turn this into 3 LinkedIn posts)"
              value={desiredNext}
              onChange={(e) => setDesiredNext(e.target.value)}
              data-testid={`desired-${k.knowledge_id}`}
              className="w-full border border-[#E2E4E9] focus:border-black p-2 text-xs outline-none"
            />
            <button onClick={saveNote} data-testid={`save-note-${k.knowledge_id}`} className="text-xs bg-[#0A0A0B] text-white px-3 py-1">Save</button>
          </div>
        )}
      </div>
    </div>
  );
}
